import React, { useCallback, useMemo, useState } from "react";
import Button from "@scripts/react/components/Button";
import { useModel } from "@/contexts/ModelContext";
import { CardHeader } from "./CardHeader";
import { CardThumbnails, Thumbnail } from "./CardThumbnails";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildPromptSteps } from "@/types/flows";
import { useFlow } from "@/contexts/FlowContext";
import { Prediction } from "@utils/api/inference";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent, AppSource } from "@/analytics/types";
import { noop } from "lodash";
import { ConfidenceSlider } from "@/components/ConfidenceSlider";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { getLabelColor, updateLabelColor } from "@/components/annotation/colors";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@/utils/predictions";
import { useTextPromptMode, useCurrentMedia } from "@/store";
import { filterByFileId, predictionsToThumbnails } from "./cardUtils";

interface PromptLabelReviewCardProps {
    name: string;
    inputRef: React.RefObject<HTMLInputElement | null>;
    onApprove: (classFilter: string) => void;
    onReject: (classFilter: string) => void;
    predictionsForThisLabel: Prediction[];
    totalLabelsCount: number;
    initialConfidenceThreshold?: number;
    mode: BuildPromptSteps;
}

export const PromptLabelReviewCard: React.FC<PromptLabelReviewCardProps> = ({
    name,
    onApprove,
    onReject,
    inputRef,
    predictionsForThisLabel,
    totalLabelsCount,
    initialConfidenceThreshold,
    mode
}) => {
    const { updatePromptLabels, promptLabels } = useModel();
    const textPromptMode = useTextPromptMode();
    const currentMedia = useCurrentMedia();
    const {
        selectedObject,
        previouslySelectedObject,
        flowLocked,
        deletePredictionFromCurrentFrame
    } = useFlow();
    const { track } = useAnalytics();
    const { showSliderHint } = useCoreApp();

    // Filter predictions to only show ones from the current file
    const predictionsForCurrentFile = useMemo(
        () => filterByFileId(predictionsForThisLabel, currentMedia?.file.id),
        [predictionsForThisLabel, currentMedia?.file.id]
    );

    const predictionCount = predictionsForCurrentFile.length;

    // Convert predictions to thumbnails for display
    const thumbnails = useMemo(
        () => predictionsToThumbnails(predictionsForCurrentFile),
        [predictionsForCurrentFile]
    );

    const handleApprove = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onApprove(name);
        },
        [onApprove, name]
    );

    const handleReject = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onReject(name);
        },
        [onReject, name]
    );

    const handleDeletePredictionClick = useCallback(
        (e: React.MouseEvent, thumbnail: Thumbnail) => {
            e.stopPropagation(); // Important: Stop event from bubbling to parent elements

            if (!thumbnail || !thumbnail.id) {
                console.warn("Attempted to delete prediction missing id", thumbnail);
                return;
            }

            // Track prediction deletion from thumbnail
            track({
                event: AnalyticsFlowEvent.PREDICTION_OBJECT_DELETED,
                data: {
                    appSource: AppSource.RAPID,
                    source: "thumbnail",
                    deletionMethod: "thumbnail_click",
                    predictionId: thumbnail.id,
                    predictionLabel: name,
                    remainingPredictions: predictionsForThisLabel.length - 1
                }
            });

            // Delete the prediction from the current frame
            deletePredictionFromCurrentFrame(thumbnail.id);
        },
        [deletePredictionFromCurrentFrame, track, name, predictionsForThisLabel.length]
    );

    const labelColor = getLabelColor(promptLabels, name);

    return (
        <div
            className={twClassNames(
                "promptCard",
                flowLocked || textPromptMode ? "cursor-default" : "cursor-pointer"
            )}
            style={
                {
                    "--tw-ring-color": showSliderHint ? `${labelColor}40` : "transparent"
                } as React.CSSProperties
            }
        >
            <div className="flex flex-row justify-between border-b border-gray-300 pb-3 pr-3">
                <CardHeader
                    name={name}
                    mode={BuildPromptSteps.REVIEW_PREDICTIONS}
                    flowLocked={flowLocked}
                    isEditing={false}
                    editValue=""
                    inputRef={inputRef}
                    onEditClick={noop}
                    onEditKeyDown={noop}
                    onEditBlur={noop}
                    setEditValue={noop}
                    labelColor={labelColor}
                    onColorChange={(newColor) =>
                        updateLabelColor(updatePromptLabels, promptLabels, name, newColor)
                    }
                />
                {/* Header buttons for Review mode - only show if there are multiple labels */}
                {totalLabelsCount > 1 && (
                    <div className="flex flex-row gap-1">
                        {/* @ts-expect-error - Button component type issue */}
                        <Button
                            onClick={handleReject}
                            type="reject-ghost"
                            size="xxsmall"
                            shape="square"
                            aria-label="Reject predictions for this class"
                            title="Reject predictions for this class"
                        >
                            <i className="far fa-xmark"></i>
                        </Button>
                        {/* @ts-expect-error - Button component type issue */}
                        <Button
                            onClick={handleApprove}
                            type="success-ghost"
                            size="xxsmall"
                            shape="square"
                            aria-label="Approve predictions for this class"
                            title="Approve predictions for this class"
                        >
                            <i className="far fa-check"></i>
                        </Button>
                    </div>
                )}
            </div>
            {!(flowLocked && predictionsForThisLabel.length === 0) && (
                <>
                    <ConfidenceSlider
                        initialConfidenceThreshold={initialConfidenceThreshold}
                        onConfidenceThresholdChanged={(newThreshold) => {
                            updatePromptLabels([{ name, confidenceThreshold: newThreshold }]);
                        }}
                        name={name}
                        predictionCount={predictionCount}
                        mode={mode}
                        className="pb-2 pt-1"
                    />
                    <CardThumbnails
                        name={name}
                        mode={BuildPromptSteps.REVIEW_PREDICTIONS}
                        thumbnails={thumbnails}
                        selectedObject={selectedObject}
                        previouslySelectedObject={previouslySelectedObject}
                        flowLocked={flowLocked}
                        onDeleteAnnotationClick={handleDeletePredictionClick}
                        labelColor={labelColor}
                    />
                </>
            )}
        </div>
    );
};
